import batchFetch from '../util/batchFetch';
import { Channels } from '../util/constants';
import ApiService from './api-service';
import { NO_TEMPLATE } from './manage-service';

const ScheduleService = {
  async checkChannelInSchedule({ shortCode, channels }) {
    const body = {
      query: `query {
        organisation {
          schedules {
            chasers {
              channel
            }
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return channels.every(channel =>
      data.organisation.schedules.some(c => c.chasers.find(ch => ch.channel === channel)),
    );
  },
  async getSchedules(shortCode) {
    const body = {
      query: `query {
        organisation {
          ShortCode
          defaultSchedule { _id, name }
          schedules {
            _id
            name
            isDefault
            customers
            balances {
              outstanding
              overdue
            }
            invoices
            senders {
              escalated {
                from
                cc
              }
              regular {
                from
                cc
              }
            }
            reminders {
              days
              assignee {
                _id
                profile {
                  firstName
                  lastName
                }
              }
              overdueLimit
              type
            }
            chasers {
              days
              template
              multiTemplate
              escalateSenders
              escalateRecipients
              repeat {
                days
                count
              }
              channel
              suggestion
            }
            paid {
              template
              multiTemplate
              channel
            }
            monthlyStatement {
              active
              day
              template {
                name
              }
            }
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return Object.assign(data.organisation.schedules, {
      defaultSchedule: data.organisation.defaultSchedule,
    });
  },
  async addChannel(shortCode, scheduleId, values, lastDay) {
    switch (values._adding) {
      case Channels.TASK:
        return this.addReminder(shortCode, scheduleId, values);
      case Channels.SMS:
      case Channels.EMAIL:
      case Channels.PHONE_CALL:
      case Channels.LETTER:
        return this.addChaser(shortCode, scheduleId, values, lastDay);
      default:
        throw new Error('Unknown channel type');
    }
  },
  async addReminder(shortCode, scheduleId, values) {
    const body = {
      query: `mutation(
        $scheduleId: String!,
        $days: Int!,
        $assignee: String,
        $type: String!,
        $overdueLimit: Int,
      ) {
        addReminder(
          scheduleId: $scheduleId,
          days: $days,
          assignee: $assignee,
          overdueLimit: $overdueLimit
          type: $type,
        ) {
          _id
        }
      }
    `,
      variables: {
        scheduleId,
        days: values.days,
        assignee: values.assignee,
        overdueLimit: values.overdueLimit,
        type: values.type,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.addReminder;
  },
  async setScheduleSenders(shortCode, scheduleId, senders) {
    const body = {
      query: `mutation(
        $scheduleId: String!,
        $senders: ScheduleSendersInput,
      ) {
        setScheduleSenders(
          scheduleId: $scheduleId,
          senders: $senders,
        ) {
          regular {
            from
            cc
          }
          escalated {
            from
            cc
          }
        }
      }
    `,
      variables: {
        scheduleId,
        senders,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.setScheduleSenders;
  },
  async addChaser(shortCode, scheduleId, values, lastDay) {
    const channelsWithRepeat = [Channels.EMAIL, Channels.SMS, Channels.PHONE_CALL];
    const repeatOptionalProperty =
      channelsWithRepeat.includes(values._adding) && values.repeatIntervalChecked
        ? {
            days: values.repeatInterval,
            count: values.repeatCountChecked ? values.repeatCount : undefined,
          }
        : undefined;

    const body = {
      query: `mutation(
        $scheduleId: String!,
        $days: Int!,
        $template: NonEmptyString!,
        $multiTemplate: NonEmptyString,
        $escalateSenders: Boolean,
        $escalateRecipients: Boolean,
        ${repeatOptionalProperty ? '$repeat: AddChaserRepeat' : ''}
      ) {
        addChaser(
          scheduleId: $scheduleId,
          days: $days,
          template: $template,
          multiTemplate: $multiTemplate,
          escalateSenders: $escalateSenders,
          escalateRecipients: $escalateRecipients,
          ${repeatOptionalProperty ? 'repeat: $repeat' : ''}
        ) {
          _id
        }
      }
    `,
      variables: {
        scheduleId,
        days: values.days,
        template: values.template,
        multiTemplate:
          values.multiTemplate !== '' && values.multiTemplate !== NO_TEMPLATE
            ? values.multiTemplate
            : undefined,
        escalateSenders: values.escalateSenders,
        escalateRecipients: values.escalateRecipients,
        repeat:
          values.days > lastDay && repeatOptionalProperty ? repeatOptionalProperty : undefined,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.addChaser;
  },
  async editChannel(shortCode, scheduleId, values, lastDay) {
    switch (values.channel) {
      case Channels.TASK:
        return this.editReminder(shortCode, scheduleId, values);
      case Channels.SMS:
      case Channels.EMAIL:
      case Channels.PHONE_CALL:
      case Channels.LETTER:
        return this.editChaser(shortCode, scheduleId, values, lastDay);
      default:
        throw new Error('Unknown channel type');
    }
  },
  async editReminder(shortCode, scheduleId, values) {
    const body = {
      query: `mutation(
        $scheduleId: String!,
        $currentDays: Int!,
        $days: Int!,
        $assignee: String,
        $type: String!,
        $overdueLimit: Int,
      ) {
        editReminder(
          scheduleId: $scheduleId
          currentDays: $currentDays,
          type: $type,
          days: $days,
          assignee: $assignee,
          overdueLimit: $overdueLimit
        ) {
          _id
        }
      }
    `,
      variables: {
        scheduleId,
        currentDays: values.currentDays,
        days: values.days,
        assignee: values.assignee,
        overdueLimit: values.overdueLimit,
        type: values.type,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.editReminder;
  },
  async editChaser(shortCode, scheduleId, values) {
    const body = {
      query: `mutation(
        $scheduleId: String!,
        $currentDays: Int,
        $days: Int!,
        $template: NonEmptyString!,
        $multiTemplate: NonEmptyString,
        $escalateSenders: Boolean,
        $escalateRecipients: Boolean,
        $repeat: EditChaserRepeat
      ) {
        editChaser(
          scheduleId: $scheduleId,
          currentDays: $currentDays,
          days: $days,
          template: $template,
          multiTemplate: $multiTemplate,
          escalateSenders: $escalateSenders,
          escalateRecipients: $escalateRecipients,
          repeat: $repeat
        ) {
          _id
        }
      }
    `,
      variables: {
        scheduleId,
        currentDays: values.currentDays,
        days: values.days,
        template: values.template,
        multiTemplate:
          !!values.multiTemplate && values.multiTemplate !== NO_TEMPLATE
            ? values.multiTemplate
            : undefined,
        escalateSenders: values.escalateSenders,
        escalateRecipients: values.escalateRecipients,
        repeat:
          [Channels.EMAIL, Channels.SMS, Channels.PHONE_CALL].includes(values.channel) &&
          values.repeatIntervalChecked
            ? {
                days: values.repeatInterval,
                count: values.repeatCountChecked ? values.repeatCount : undefined,
              }
            : undefined,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.addChaser;
  },

  async setTFP(shortCode, scheduleId, values) {
    const body = {
      query: `mutation(
        $scheduleId: String!,
        $template: NonEmptyString,
        $multiTemplate: NonEmptyString,
      ) {
        setTFP(
          scheduleId: $scheduleId,
          template: $template,
          multiTemplate: $multiTemplate,
        ) {
          _id
        }
      }
    `,
      variables: {
        scheduleId,
        template: !!values.template && values.template !== NO_TEMPLATE ? values.template : null,
        multiTemplate:
          !!values.multiTemplate && values.multiTemplate !== NO_TEMPLATE
            ? values.multiTemplate
            : null,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.addChaser;
  },
  async createSchedule(shortCode, { name, from }) {
    const body = {
      query: `mutation createSchedule($name: String!, $from: String!) {
        createSchedule(
          name: $name,
          from: $from
        ) {
          _id
          name
        }
      }
      `,
      variables: { name, from },
    };
    const { createSchedule } = await ApiService.orgEndpoint(shortCode).post('/', body);
    return createSchedule;
  },
  async removeSchedule(scheduleId, shortCode) {
    const body = {
      query: `mutation deleteSchedule($scheduleId: String!) {
        deleteSchedule(scheduleId: $scheduleId)
      }`,
      variables: { scheduleId },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async removeChannel(shortCode, { scheduleId, channel, assignee, days, type }) {
    switch (channel) {
      case Channels.TASK:
        return this.removeReminder(shortCode, {
          scheduleId,
          days,
          assignee,
          type,
        });
      case Channels.PHONE_CALL:
      case Channels.SMS:
      case Channels.EMAIL:
      case Channels.LETTER:
        return this.removeChaser(shortCode, { scheduleId, chaserDays: days });
      default:
        throw new Error('Unknown channel type');
    }
  },
  async removeChaser(shortCode, { scheduleId, chaserDays }) {
    const body = {
      query: `mutation deleteChaser($scheduleId: String!, $chaserDays: Int!) {
        deleteChaser(
          scheduleId: $scheduleId,
          days: $chaserDays,
        ), {
          name
          }
        }`,
      variables: { scheduleId, chaserDays },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async removeReminder(shortCode, { scheduleId, days, assignee, type }) {
    const body = {
      query: `mutation deleteReminder($scheduleId: String!, $days: Int!, $type: String!, $assignee: String) {
        deleteReminder(
          scheduleId: $scheduleId,
          days: $days,
          type: $type,
          assignee: $assignee,
        ), {
          name
          }
        }`,
      variables: { scheduleId, days, type, assignee },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async remameSchedule(shortCode, { scheduleId, scheduleName }) {
    const body = {
      query: `mutation updateScheduleName($scheduleId: String!, $name: String!) {
        updateScheduleName(scheduleId: $scheduleId, name: $name) {
          _id
          name
        }
      }`,
      variables: { scheduleId, name: scheduleName },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async makeDefaultSchedule(shortCode, { scheduleId, moveEntities }) {
    const body = {
      query: `mutation setDefaultSchedule($scheduleId: String!, $moveEntities: Boolean!) {
        setDefaultSchedule(scheduleId: $scheduleId, moveEntities: $moveEntities)
      }`,
      variables: { scheduleId, moveEntities },
    };

    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
  async setSchedule(shortCode, scheduleId, { invoiceIds, contactIds, updateContactInvoices }) {
    const batchContacts = contactIds?.length ?? 0 > invoiceIds?.length ?? 0;
    await batchFetch(
      async ids => {
        const body = {
          query: `mutation setSchedule($contactIds: [String], $invoiceIds: [String], $scheduleId: String!, $updateContactInvoices: Boolean) {
        setSchedule(contactIds: $contactIds, invoiceIds: $invoiceIds, scheduleId: $scheduleId, updateContactInvoices: $updateContactInvoices)
      }`,
          variables: {
            scheduleId,
            invoiceIds: batchContacts ? invoiceIds : ids,
            contactIds: batchContacts ? ids : contactIds,
            updateContactInvoices,
          },
        };

        const res = await ApiService.orgEndpoint(shortCode).post('/', body);
        if (!res.setSchedule) throw new Error('Failed to update schedule');
      },
      batchContacts ? contactIds : invoiceIds,
      { toastMessage: 'Updating schedule' },
    );
  },
  async setMonthlyStatement(shortCode, scheduleId, { active, day, template }) {
    const body = {
      query: `mutation setMonthlyStatement($scheduleId: String!, $day: Int!, $template: String!, $active: Boolean!) {
        setMonthlyStatement(scheduleId: $scheduleId, day: $day, template: $template, active: $active) {
          _id
        }
      }`,
      variables: { scheduleId, active, day, template },
    };
    await ApiService.orgEndpoint(shortCode).post('/', body);
  },
};

export default ScheduleService;
